/**
 * ScaffoldAnswerInput Component
 * US-005 (FR-6.4 / COMP-002): Inline sentence-completion (type_12) answer input.
 *
 * Renders the question stem as a flowing sentence with a single inline text field
 * positioned where the blank marker was. The blank position (beginning / middle / end)
 * is encoded entirely by which of stemPrefix / stemSuffix is empty:
 *   - stemPrefix === ''  -> blank at the BEGINNING (input before the stem)  [AC-005-3]
 *   - stemSuffix === ''  -> blank at the END       (input after the stem)   [AC-005-1]
 *   - both non-empty     -> blank in the MIDDLE     (input between parts)    [AC-005-2]
 *
 * Submits ONLY the student's trimmed completion text, never the reconstructed full
 * sentence (AC-005-4). Disabled while a learning-tip popup is open / submitting /
 * waiting for tutor (AC-005-5) — mirrors AnswerInput/TableAnswerInput's isDisabled gate.
 * Input carries aria-label "Complete the sentence" + a 44px (h-11) touch target
 * (AC-005-6 / NFR-5 / NFR-6).
 *
 * NOTE (intentional COMP-002 refinement): props are stemPrefix/stemSuffix, NOT
 * stemText/blankPosition — a single string + enum cannot express a middle blank.
 *
 * Implements US-005; enforces TO-LL-044 (camelCase fields read at the boundary — stem
 * text comes from message.content as delivered, no aliasing).
 */

import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useViewportHeight } from '@/hooks/useViewportHeight';

/**
 * Splits a type_12 question stem at its FIRST blank marker (a run of 3+ underscores).
 * Returns { prefix, suffix } around that first run, or null when no marker is present
 * (edge case 1: the panel falls back to the free-text AnswerInput — no crash).
 *
 * Only the FIRST run is consumed (AC-005-2 edge case): any later underscore run stays
 * as literal text in the suffix (the trailing stem part).
 *
 * US-005 (FR-6.4): pure helper kept here so it is unit-testable in isolation,
 * mirroring how tableParser.ts exports parseMarkdownTable.
 */
export function parseScaffoldStem(
  content: string,
): { prefix: string; suffix: string } | null {
  const match = /_{3,}/.exec(content);
  if (!match) return null;
  const start = match.index;
  const end = start + match[0].length;
  return {
    prefix: content.slice(0, start),
    suffix: content.slice(end),
  };
}

export interface ScaffoldAnswerInputProps {
  /** Stem text BEFORE the blank ('' when the blank is at the sentence start). */
  stemPrefix: string;
  /** Stem text AFTER the blank ('' when the blank is at the sentence end). */
  stemSuffix: string;
  /** Callback fired on submit — receives the trimmed completion text ONLY (AC-005-4). */
  /**
   * Submit handler. BUG-20260721: may resolve `false` to signal the turn was
   * rejected, in which case the completion text is restored rather than lost.
   */
  onSubmit: (content: string) => void | Promise<boolean>;
  /** True while a submission is in progress. */
  isSubmitting: boolean;
  /** True while waiting for the tutor to reply after submit. */
  isWaitingForTutor?: boolean;
  /** True while a learning-tip popup is open — disables the field + Submit (AC-005-5). */
  isShowingTip?: boolean;
}

/**
 * ScaffoldAnswerInput — inline sentence-completion input for type_12 scaffold questions.
 *
 * Implements US-005; enforces TO-LL-028 (consumed via scaffoldInputData in ConversationPanel
 * which scans ALL messages for the current question number — stable across optimistic updates).
 */
export function ScaffoldAnswerInput({
  stemPrefix,
  stemSuffix,
  onSubmit,
  isSubmitting,
  isWaitingForTutor = false,
  // US-002 pattern: gate every control while a learning-tip popup is open (AC-005-5).
  isShowingTip = false,
}: ScaffoldAnswerInputProps) {
  // Mobile keyboard handling (same hook AnswerInput/TableAnswerInput use).
  useViewportHeight();

  const [value, setValue] = useState('');

  // Same gate the other inputs use (isSubmitting || isWaitingForTutor || isShowingTip).
  const isDisabled = isSubmitting || isWaitingForTutor || isShowingTip;

  // Enabled only when there is non-whitespace completion text.
  const canSubmit = !isDisabled && value.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // AC-005-4: submit ONLY the completion text (never the reconstructed sentence).
    const trimmed = value.trim();

    // Clear optimistically, then restore if the turn was rejected (BUG-20260721) —
    // same contract as AnswerInput, see the fuller rationale there.
    setValue('');

    const accepted = await onSubmit(trimmed);

    if (accepted === false) {
      setValue((current) => (current === '' ? trimmed : current));
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-[#E7D8C8] bg-otter-paper p-3 md:p-4 sticky bottom-0 z-10 pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-3xl mx-auto">
        {/* Sentence-flow stem with the inline blank. prefix / input / suffix all flow on
            the same line; the input does NOT break onto its own line. Empty-string
            prefix/suffix render nothing (React renders '' as no text node).
            Implements AC-005-1 (end blank), AC-005-2 (middle blank), AC-005-3 (start blank). */}
        <p className="text-base text-otter-earth-700 leading-relaxed mb-3">
          {stemPrefix && <span>{stemPrefix}</span>}
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            aria-label="Complete the sentence"
            placeholder="your answer"
            className={cn(
              'inline-block align-baseline mx-1 h-11 md:h-10 min-w-[120px] px-2',
              'rounded-md border-2 border-otter-sand bg-white text-otter-earth-700',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-500',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
          {stemSuffix && <span>{stemSuffix}</span>}
        </p>

        {/* Waiting indicator (mirrors TableAnswerInput / MCQAnswerInput). */}
        {isWaitingForTutor && (
          <p className="text-sm text-pebbles-500 text-center mb-2">Waiting for tutor...</p>
        )}

        {/* Submit button: h-11 mobile / h-10 md (NFR-6 / AC-005-6). */}
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full h-11 md:h-10"
          aria-label="Submit answer"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending
            </>
          ) : (
            'Submit Answer'
          )}
        </Button>
      </div>
    </div>
  );
}
